import React, { useMemo } from 'react';
import { startCase } from 'lodash';
import { colors } from '@/styles';
import { Grid } from '@material-ui/core';
import { css } from '@emotion/react';
import IconButton from '@material-ui/core/IconButton';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@material-ui/icons/Close';
import Popover from '@material-ui/core/Popover';
import Button from '@material-ui/core/Button';
import { CheckCircleOutline, HelpOutline, HighlightOffOutlined, SyncOutlined, EditOutlined } from '@material-ui/icons';
import { asBrowserDate } from '@/utilities/convertToISO';
import { formatPayPeriod, editsDueDate } from '@/utilities/payPeriod';
import { NetsuiteLink } from '@/components/NetsuiteLink';
import { BillcomLink } from '@/components/BillcomLink';
import { BodyText } from '@/components/CarrierAccountingOrders/blocks';
import { calcSupplementalTotal } from '@onward-delivery/core';
import { countOrdersMissing210, hasAnySubmitted210, allSuccessful210 } from '@/utilities/orderful';

const dateNumeric = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

const dollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

export const useColumns = ({ callbacks, isAdmin, canViewIntegrations }) => {
    return useMemo(() => {
        return [
            {
                Header: 'Data',
                id: 'data-columns',
                columns: [
                    {
                        Header: 'Invoice',
                        id: 'invoice_number',
                        accessor: (invoice) => invoice.invoice_number || '--',
                        width: 75,
                    },
                    {
                        Header: 'Carrier',
                        id: 'business_name',
                        span: false,
                        accessor: (invoice) => invoice.client?.business_name,
                        Cell: ({ value }) => {
                            return (
                                <div
                                    css={css`
                                        overflow: hidden;
                                        height: 100%;
                                    `}
                                >
                                    <BodyText
                                        css={css`
                                            white-space: normal;
                                        `}
                                    >
                                        {value}
                                    </BodyText>
                                </div>
                            );
                        },
                    },
                    {
                        Header: 'Week',
                        id: 'week_number',
                        accessor: (invoice) => invoice.week_number,
                        width: 75,
                    },
                    {
                        Header: 'Pay Period',
                        id: 'period',
                        span: false,
                        accessor: (invoice) => {
                            return formatPayPeriod(invoice.pay_period_start, invoice.pay_period_end) || '--';
                        },
                        Cell: ({ value }) => {
                            return (
                                <div
                                    css={css`
                                        overflow: hidden;
                                        height: 100%;
                                    `}
                                >
                                    <BodyText
                                        css={css`
                                            white-space: normal;
                                        `}
                                    >
                                        {value}
                                    </BodyText>
                                </div>
                            );
                        },
                    },
                    {
                        Header: 'Edits Due',
                        id: 'edits_due',
                        span: false,
                        accessor: (invoice) => {
                            const thurs = editsDueDate(invoice.pay_period_end);
                            if (!thurs) return '--';

                            if (!invoice.is_pending) {
                                return `${dateNumeric.format(new Date(thurs))} (submitted)`;
                            }

                            return dateNumeric.format(thurs);
                        },
                        Cell: ({ value }) => {
                            return (
                                <div
                                    css={css`
                                        overflow: hidden;
                                        height: 100%;
                                    `}
                                >
                                    <BodyText
                                        css={css`
                                            white-space: normal;
                                        `}
                                    >
                                        {value}
                                    </BodyText>
                                </div>
                            );
                        },
                    },
                    {
                        Header: 'Due',
                        id: 'due_date',
                        width: 100,
                        disableSortBy: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            return invoice.due_date ? dateNumeric.format(asBrowserDate(invoice.due_date)) : '--';
                        },
                    },
                    {
                        Header: 'POD',
                        id: 'pod',
                        width: 75,
                        Cell: ({ row }) => {
                            const invoice = row.original;

                            const isMissing = [
                                'orders',
                                'middle_mile_carrier_orders',
                                'warehouse_carrier_orders',
                                'contractor_orders',
                            ].some((ordersAttr) => {
                                return (invoice[ordersAttr] || []).some((order) => {
                                    const hasPhoto = order.delivery_photo?.length >= 1 || order.pod?.length >= 1;
                                    const hasSignature = order.customer_signature?.length >= 1;
                                    return !hasSignature && !hasPhoto;
                                });
                            });

                            return !isMissing ? (
                                <>
                                    <DoneIcon
                                        css={css`
                                            color: ${colors.greens.primary};
                                        `}
                                    />
                                </>
                            ) : (
                                <CloseIcon
                                    css={css`
                                        color: ${colors.reds.primary};
                                        background: none;
                                    `}
                                />
                            );
                        },
                    },
                    {
                        Header: 'Status',
                        id: 'status',
                        width: 125,
                        span: false,
                        disableSortBy: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            const label = startCase(invoice.status.toLowerCase());
                            let Icon = HelpOutline;
                            let color = 'black';
                            switch (invoice.status) {
                                case 'PAID':
                                    color = colors.greens.primary;
                                    Icon = CheckCircleOutline;
                                    break;
                                case 'APPROVED':
                                    color = colors.greens.primary;
                                    Icon = CheckCircleOutline;
                                    break;
                                case 'UNPAID':
                                case 'CANCELLED':
                                    color = colors.reds.cancelled;
                                    Icon = HighlightOffOutlined;
                                    break;
                                case 'PROCESSING':
                                    color = colors.oranges.primary;
                                    Icon = SyncOutlined;
                                    break;
                                case 'DRAFT':
                                    color = colors.greys.secondary;
                                    Icon = EditOutlined;
                                    break;
                                default:
                                    color = 'black';
                                    Icon = HelpOutline;
                                    break;
                            }

                            return (
                                <Grid
                                    container
                                    direction="row"
                                    css={css`
                                        color: ${color};
                                        height: 100%;
                                        align-items: center;
                                        flex-wrap: nowrap;
                                    `}
                                >
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            flex: 0;
                                            flex-basis: 0;
                                            margin-right: 4px;
                                        `}
                                    >
                                        <Icon />
                                    </Grid>
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            flex: 0;
                                            flex-basis: 0;
                                        `}
                                    >
                                        <BodyText>{label}</BodyText>
                                    </Grid>
                                </Grid>
                            );
                        },
                    },
                    {
                        Header: 'Subtotal',
                        id: 'subtotal',
                        width: 125,
                        canSort: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;

                            const carrierAmount = (invoice.orders || []).reduce((acc, order) => {
                                const total = order.carrier_rate || 0;
                                return acc + total;
                            }, 0);
                            const mmCarrierAmount = (invoice.middle_mile_carrier_orders || []).reduce((acc, order) => {
                                const total = order.middle_mile_carrier_rate || 0;
                                return acc + total;
                            }, 0);
                            const whCarrierAmount = (invoice.warehouse_carrier_orders || []).reduce((acc, order) => {
                                const total = order.warehouse_carrier_rate || 0;
                                return acc + total;
                            }, 0);
                            const contractorAmount = (invoice.contractor_orders || []).reduce((acc, order) => {
                                const total = order.contractor_rate || 0;
                                return acc + total;
                            }, 0);
                            const amount = carrierAmount + mmCarrierAmount + whCarrierAmount + contractorAmount;

                            return dollarFormatter.format(amount);
                        },
                    },
                    {
                        Header: 'Claims',
                        id: 'claims',
                        width: 125,
                        canSort: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            const claimsTotal = (invoice.claim_adjustments || []).reduce((acc, { amount }) => {
                                return acc + amount;
                            }, 0);
                            return `(${dollarFormatter.format(claimsTotal)})`;
                        },
                    },
                    {
                        Header: 'Supplemental Charges',
                        id: 'supplemental_charges',
                        width: 125,
                        canSort: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            return dollarFormatter.format(calcSupplementalTotal(invoice));
                        },
                    },
                    {
                        Header: 'Total Due',
                        id: 'revenue',
                        width: 125,
                        canSort: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;

                            const carrierAmount = (invoice.orders || []).reduce((acc, order) => {
                                const total = order.carrier_rate || 0;
                                return acc + total;
                            }, 0);
                            const mmCarrierAmount = (invoice.middle_mile_carrier_orders || []).reduce((acc, order) => {
                                const total = order.middle_mile_carrier_rate || 0;
                                return acc + total;
                            }, 0);
                            const whCarrierAmount = (invoice.warehouse_carrier_orders || []).reduce((acc, order) => {
                                const total = order.warehouse_carrier_rate || 0;
                                return acc + total;
                            }, 0);
                            const contractorAmount = (invoice.contractor_orders || []).reduce((acc, order) => {
                                const total = order.contractor_rate || 0;
                                return acc + total;
                            }, 0);
                            const ordersTotal = carrierAmount + mmCarrierAmount + whCarrierAmount + contractorAmount;

                            const claimsTotal = (invoice.claim_adjustments || []).reduce((acc, { amount }) => {
                                return acc + amount;
                            }, 0);

                            return dollarFormatter.format(ordersTotal - claimsTotal + calcSupplementalTotal(invoice));
                        },
                    },
                    ...(canViewIntegrations
                        ? [
                              {
                                  Header: 'NetSuite',
                                  id: 'netsuite',
                                  width: 100,
                                  canSort: false,
                                  Cell: ({ row }) => <NetsuiteLink invoice={row.original} />,
                              },
                              // Payables only. Bill.com never receives a receivable, which is why
                              // the receivables table has no counterpart column.
                              {
                                  Header: 'Bill.com',
                                  id: 'billcom',
                                  width: 100,
                                  canSort: false,
                                  Cell: ({ row }) => <BillcomLink invoice={row.original} />,
                              },
                              {
                                  Header: 'Orderful',
                                  id: 'orderful',
                                  width: 100,
                                  canSort: false,
                                  Cell: ({ row }) => {
                                      const invoice = row.original;
                                      const orders = [
                                          ...(invoice.orders || []),
                                          ...(invoice.middle_mile_carrier_orders || []),
                                          ...(invoice.warehouse_carrier_orders || []),
                                          ...(invoice.contractor_orders || []),
                                      ];
                                      if (!hasAnySubmitted210(invoice, orders)) {
                                          return '--';
                                      }

                                      const missing = countOrdersMissing210(invoice, orders);
                                      if (missing > 0) {
                                          return (
                                              <BodyText
                                                  css={css`
                                                      font-weight: bold;
                                                      color: ${colors.reds.primary};
                                                  `}
                                              >
                                                  {missing}
                                              </BodyText>
                                          );
                                      }

                                      return allSuccessful210(invoice, orders) ? (
                                          <DoneIcon
                                              css={css`
                                                  color: ${colors.greens.primary};
                                              `}
                                          />
                                      ) : (
                                          <CloseIcon
                                              css={css`
                                                  color: ${colors.reds.primary};
                                                  background: none;
                                              `}
                                          />
                                      );
                                  },
                              },
                          ]
                        : []),
                ],
            },
            {
                sticky: 'right',
                Header: 'Sticky',
                id: 'sticky-group',
                columns: [
                    {
                        Header: '',
                        id: 'action-1',
                        width: 45,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            const [anchorEl, setAnchorEl] = React.useState(null);

                            const handleClick = (event) => {
                                event.stopPropagation();
                                event.preventDefault();
                                setAnchorEl(event.currentTarget);
                            };

                            const handleClose = (event) => {
                                event?.stopPropagation();
                                event?.preventDefault();
                                setAnchorEl(null);
                            };

                            return (
                                <div
                                    css={css`
                                        color: #59b863;
                                        font-weight: 600;
                                        display: flex-start;
                                        justify-content: flex-start;
                                        align-items: center;
                                    `}
                                >
                                    <IconButton
                                        disabled={invoice.status === 'PAID'}
                                        onClick={handleClick}
                                        css={css`
                                            font-weight: 600;
                                            padding: 4px;
                                            justify-content: center;
                                        `}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Popover
                                        open={Boolean(anchorEl)}
                                        anchorEl={anchorEl}
                                        onClose={handleClose}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'center',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'center',
                                        }}
                                        style={{ padding: '10px', borderRadius: '20px' }}
                                    >
                                        <Grid direction="column" container>
                                            {isAdmin && ['DRAFT'].includes(invoice.status) && (
                                                <Grid direction="row" container>
                                                    <Button
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            callbacks.submitId(invoice.carrier_invoice_id);
                                                            handleClose();
                                                        }}
                                                        css={css`
                                                            width: 100%;
                                                            justify-content: flex-start;
                                                        `}
                                                    >
                                                        Submit
                                                    </Button>
                                                </Grid>
                                            )}
                                            {isAdmin && ['UNPAID'].includes(invoice.status) && (
                                                <Grid direction="row" container>
                                                    <Button
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            callbacks.approveId(invoice.carrier_invoice_id);
                                                            handleClose();
                                                        }}
                                                        css={css`
                                                            width: 100%;
                                                            justify-content: flex-start;
                                                        `}
                                                    >
                                                        Approve
                                                    </Button>
                                                </Grid>
                                            )}
                                            {isAdmin && ['UNPAID', 'APPROVED'].includes(invoice.status) && (
                                                <Grid direction="row" container>
                                                    <Button
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            callbacks.payId(invoice.carrier_invoice_id);
                                                            handleClose();
                                                        }}
                                                        css={css`
                                                            width: 100%;
                                                            justify-content: flex-start;
                                                        `}
                                                    >
                                                        Mark as Paid
                                                    </Button>
                                                </Grid>
                                            )}
                                            {isAdmin && !['APPROVED', 'PAID'].includes(invoice.status) && (
                                                <Grid direction="row" container>
                                                    <Button
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            callbacks.cancelId(invoice.carrier_invoice_id);
                                                            handleClose();
                                                        }}
                                                        css={css`
                                                            width: 100%;
                                                            justify-content: flex-start;
                                                        `}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Popover>
                                </div>
                            );
                        },
                        span: false,
                    },
                ],
            },
        ];
    }, [callbacks, isAdmin, canViewIntegrations]);
};

export const QUICKBOOKS_CSV_COLUMNS = [
    {
        header: 'Invoice No.',
        getValue: ({ invoice }) => invoice.invoice_number,
    },
    {
        header: 'Carrier',
        getValue: ({ invoice }) => invoice.client.business_name,
    },
    {
        header: 'Invoice Date',
        getValue: ({ invoice }) => {
            if (!invoice.created_at) {
                return undefined;
            }
            const date = new Date(invoice?.created_at);
            return isNaN(date) ? undefined : date;
        },
        format: (value) => `"${dateNumeric.format(value)}"`,
    },
    {
        header: 'Due Date',
        getValue: ({ invoice }) => {
            if (!invoice.due_date) {
                return undefined;
            }
            const date = new Date(invoice?.due_date);
            return isNaN(date) ? undefined : date;
        },
        format: (value) => `"${dateNumeric.format(value)}"`,
    },
    {
        header: 'Date Completed',
        getValue: ({ order }) => {
            if (!order.billing_completion_time) {
                return undefined;
            }
            const date = new Date(order.billing_completion_time);
            return isNaN(date) ? undefined : date;
        },
        format: (value) => `"${dateNumeric.format(value)}"`,
    },
    {
        header: 'Tracking No.',
        getValue: ({ order }) => order.order_number,
    },
    {
        header: 'Purchase Order',
        getValue: ({ order }) => [order.po_number, order.reference_id].filter(Boolean).join(' / '),
    },
    {
        header: 'Product/Service',
        getValue: ({ row }) => {
            return row?.qbo_tag || '';
        },
    },
    {
        header: 'Description',
        getValue: ({ row }) => {
            return row?.description || '';
        },
    },
    {
        header: 'Qty',
        getValue: ({ row }) => {
            return row?.quantity || '';
        },
    },
    {
        header: 'Rate',
        getValue: ({ row }) => {
            return dollarFormatter.format(row?.rate || 0);
        },
    },
    {
        header: 'Amount',
        getValue: ({ row }) => {
            return dollarFormatter.format(row?.total || 0);
        },
    },
    {
        header: 'Class',
        getValue: ({ order }) => {
            return order?.invoice_class || '';
        },
    },
    {
        header: 'Memo',
        getValue: ({ order }) => {
            return [order.order_number, order.po_number, order.reference_id].filter(Boolean).join(' / ');
        },
    },
];
